import Mobile from "@/assets/images/adcelerate/how-it-works/mobile.png";
import Revenue from "@/assets/images/adcelerate/how-it-works/revenue.png";

import { Card } from "@/components/ui/card";
import useCountUp from "@/hooks/useCountUp";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { formatWithCommas } from "@/utils/formatNumber";
import startTypewriter from "@/utils/typewriterAnimation";
import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Button from "../ui/button";

export default function AdceHowItWorks() {
  const { ref, isVisible } = useScrollReveal();

  const revenue = useCountUp(5489, 2000);

  const creators = [
    "/videos/adcelerate/video-1.mp4",
    "/videos/adcelerate/video-2.mp4",
    "/videos/adcelerate/video-3.mp4",
    "/videos/adcelerate/video-4.mp4",
    "/videos/adcelerate/video-5.mp4",
    "/videos/adcelerate/video-6.mp4",
    "/videos/adcelerate/video-7.mp4",
    "/videos/adcelerate/video-8.mp4",
    "/videos/adcelerate/video-9.mp4",
  ];

  useEffect(() => {
    startTypewriter({
      elementId: "howItWorksTypewriter",
      words: ["How it works"],
    });
    startTypewriter({
      elementId: "emailTypewriter",
      words: ["Enter your email here...", "example@gmail.com"],
    });
  }, []);

  return (
    <div
      ref={ref}
      id="how-it-works"
      className={`${isVisible ? "bounce-up" : ""
        } bg-[linear-gradient(to_right,var(--primary),var(--tertiary))] py-20 lg:py-16 px-10 lg:px-16 4xl:max-w-[100rem] 4xl:mx-auto`}
    >
      <div className="flex flex-col gap-20 justify-center items-center w-full">
        <h1 className="adce-hero font-bold text-4xl md:text-6xl uppercase text-white text-center">
          <span id="howItWorksTypewriter"></span>
          <span className="border-r-8 border-white animate-pulse ml-1"></span>
        </h1>
        <div className="flex flex-col lg:flex-row items-start justify-evenly w-full text-white lg:gap-14 xl:gap-20 -mt-20">
          <div className="space-y-4 w-full">
            <div className="relative pb-4 lg:h-[355px] w-full flex flex-col items-center justify-end">
              <img
                src={Mobile}
                className="md:max-w-96 lg:max-w-60 xl:max-w-72 1xl:max-w-96 mx-auto xl:my-auto"
                alt="Mobile"
              />
              <p className="absolute left-16 md:left-[230px] lg:left-12 xl:left-16 1xl:left-20 4xl:left-28 xxs:top-[211px] xs:top-[225px] sm:top-[238px] md:top-[266px] lg:top-[281px] xl:top-[236px] 1xl:top-[250px] 4xl:top-[252px] text-black">
                <span id="emailTypewriter"></span>
                <span className="border-r-2 border-black animate-pulse ml-1"></span>
              </p>
            </div>
            <h1 className="adce-hero font-bold text-xl">1. Quick E-sign</h1>
            <p>
              We ensure all clients sign a safe and secure eSign that grants us
              legal permission to perform our services only on the platforms you
              choose. You retain all content ownership rights, and our
              agreements are no-lock in with a 30-day cancel period.
            </p>
          </div>
          <div className="space-y-4 w-full">
            <div className="relative h-[341px] overflow-hidden w-full flex flex-col items-center justify-end">
              <CarouselTrack creators={creators} />
            </div>
            <h1 className="adce-hero font-bold text-xl pt-3">
              2. Your Best Videos
            </h1>
            <p>
              Our team hand-selects your top performing videos and crafts a
              sustainable publishing schedule to ensure that we do not exhaust
              your catalog, but still go aggressive enough to move the needle
              and push your reach and revenue to all new heights — sustainably.
            </p>
          </div>
          <div className="space-y-4 w-full">
            <div className="relative lg:pt-20 xl:pt-0 xl:pb-4 lg:h-[351px] flex flex-col items-center justify-center">
              <img
                src={Revenue}
                className="md:max-w-96 lg:max-w-72 xl:max-w-96 mx-auto mt-20"
                alt="Revenue"
              />
              <p
                className="absolute right-12 xs:right-14 sm:right-16 md:right-[205px] lg:right-[40px] xl:right-[48px] 1xl:right-[60px] 4xl:right-20 top-[120px] xs:top-[123px] sm:top-[127px] md:top-[130px] lg:top-[225px] xl:top-[167px] text-[#1F89DB] text-4xl md:text-5xl lg:text-4xl xl:text-5xl font-bold"
                ref={revenue.ref}
              >
                + {formatWithCommas(revenue.count)}
              </p>
            </div>
            <h1 className="adce-hero font-bold text-xl">3. Monthly Payouts</h1>
            <p>
              After our Platform Partners approve the content for monetization,
              we will begin to collect ad-revenue and pay you your portion each
              and every month.
            </p>
          </div>
        </div>
        <Link to="/esign">
          <Button className="group relative rounded-md shadow bg-primary text-white py-6 w-48 text-base flex items-center justify-center gap-2 overflow-hidden">
            <span className="transform transition-transform duration-300 group-hover:translate-x-6">
              E-sign now
            </span>
            <ArrowRight className="transform transition-transform duration-300 group-hover:-translate-x-[90px]" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

function CarouselTrack({ creators }) {
  const trackRef = useRef(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const node = trackRef.current;
    if (!node) return;
    const section = node.closest('#how-it-works');
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isOnScreen = entry.isIntersecting && entry.intersectionRatio > 0;
        setPaused(!isOnScreen);
      },
      { threshold: 0.01 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex absolute w-max" style={{ gap: '1rem' }}>
      <div
        ref={trackRef}
        className={`scroll-section-loop ${paused ? 'anim-paused' : ''}`}
        style={{ '--speed': '20000ms', willChange: 'transform', gap: '1rem' }}
      >
        {[...creators, ...creators].map((creator, idx) => (
          <div key={idx} className="flex-shrink-0 w-40 lg:w-32 xl:w-40">
            <CreatorCard creator={creator} />
          </div>
        ))}
      </div>
    </div>
  );
}

function CreatorCard({ creator }) {
  const [isVisible, setIsVisible] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const ref = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const currentlyVisible = entry.isIntersecting;
        setIsVisible(currentlyVisible);
        if (currentlyVisible && !hasLoaded) {
          setHasLoaded(true);
        }
      },
      { threshold: 0.6 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasLoaded]);

  useEffect(() => {
    const node = videoRef.current;
    if (!node) return;
    if (isVisible) {
      node.play().catch(() => { });
    } else {
      try {
        node.pause();
      } catch { }
    }
  }, [isVisible]);

  return (
    <Card
      ref={ref}
      className="relative overflow-hidden border-0 p-0 rounded-xl"
    >
      {hasLoaded ? (
        <video
          src={creator}
          ref={videoRef}
          autoPlay={isVisible}
          muted
          loop
          playsInline
          preload={isVisible ? "auto" : "none"}
          className="w-full aspect-[9/16] object-cover mx-6 rounded-xl h-[280px] lg:h-[174px] xl:h-[280px]"
          onCanPlay={(e) => {
            if (isVisible) {
              setTimeout(() => e.target.play().catch(() => { }), 100);
            }
          }}
        />
      ) : (
        <div className="bg-gray-200 animate-pulse w-full h-[280px] lg:h-[174px] xl:h-[280px] rounded-xl mx-6" />
      )}
    </Card>
  );
}
